import type {
  RefereeRelationship,
  RefereeAnswer,
} from '@/features/referee-app/types/referee-app.types';

export type { RefereeRelationship, RefereeAnswer };

export type VerificationStatus =
  'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'TIMED_OUT' | 'VERIFIED' | 'RETURNED';

export type VerificationRequestStatus =
  'DRAFT' | 'PENDING' | 'CONFIRMED' | 'DECLINED' | 'UNABLE_TO_VERIFY';

/**
 * Verification as served to company staff. The identity of the super admin who
 * inspected it stays on the server; only the return comment reaches the agent.
 */
export interface Verification {
  id: string;
  experienceId: string;
  refereeEmail: string;
  refereeName: string;
  refereeJobTitle?: string | null;
  status: VerificationStatus;
  requestStatus: VerificationRequestStatus;
  refereeComment?: string | null;
  sentAt?: string | null;
  respondedAt?: string | null;
  shareId: string;
  refereeRelationship?: RefereeRelationship | null;
  refereePhone?: string | null;
  refereeAnswers?: RefereeAnswer[] | null;
  inspectionComment?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVerificationRequest {
  refereeEmail: string;
  refereeName: string;
  refereeJobTitle?: string | null;
}

export interface ListVerificationsParams {
  candidateId: string;
  experienceId: string;
}

export const VERIFICATION_STATUS_CONFIG: Record<
  VerificationStatus,
  { label: string; color: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  DRAFT: { label: 'Draft', color: 'secondary' },
  SENT: { label: 'Sent', color: 'default' },
  ACCEPTED: { label: 'Awaiting Inspection', color: 'secondary' },
  REJECTED: { label: 'Rejected', color: 'destructive' },
  TIMED_OUT: { label: 'Timed Out', color: 'outline' },
  VERIFIED: { label: 'Verified', color: 'default' },
  RETURNED: { label: 'Returned', color: 'outline' },
};

export const REQUEST_STATUS_CONFIG: Record<
  VerificationRequestStatus,
  { label: string; color: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  DRAFT: { label: 'Draft', color: 'secondary' },
  PENDING: { label: 'Pending', color: 'outline' },
  CONFIRMED: { label: 'Confirmed', color: 'default' },
  DECLINED: { label: 'Declined', color: 'destructive' },
  UNABLE_TO_VERIFY: { label: 'Unable to verify', color: 'outline' },
};
